/**
 * Het traject waar iets bij hoort: een Project, een Kavel of een Opportunity.
 *
 * Gedeeld door `quotes` (migratie 032) en `microsoft_calendar_events`
 * (migratie 033). Beide hebben exact dezelfde drie kolommen met dezelfde
 * `<= 1`-CHECK, dus deze module hoort niet bij een van de twee.
 *
 * De uitleg hieronder is geschreven vanuit offertes, omdat daar de valkuil zit
 * die je moet kennen; voor afspraken geldt hetzelfde patroon zonder klant-as.
 *
 * ── TWEE ASSEN, NIET ÉÉN ─────────────────────────────────────────────────────
 *
 * `quotes` draagt twee soorten verwijzing naast elkaar, en dat is het verschil
 * met `laadplein_designs` (zie `design-owner.ts`), waar er maar één was:
 *
 *   klant-as    organization_id, contact_id      wie de offerte krijgt
 *   traject-as  project_id | charging_location_id | charging_opportunity_id
 *                                                 waar hij bij hoort
 *
 * `charging_location_id` is dus géén concurrent van `organization_id` maar van
 * `project_id`. Deze module gaat alleen over de traject-as. **De klant-as blijft
 * altijd gevuld** — ook een Kavel- of Opportunity-offerte houdt gewoon zijn
 * organisatie en contactpersoon. Wie hier een `organization_id` verwacht, zoekt
 * op de verkeerde plek.
 *
 * ── WAAROM DRIE ──────────────────────────────────────────────────────────────
 *
 * Een Kavel ontwikkelt Greenvision op eigen risico. De ontvanger van een offerte
 * daarop is een vlooteigenaar die op die hub gaat laden — een Opportunity, met
 * eigen organisatie en contactpersoon. Daarom is `charging_opportunity_id` de
 * scherpste koppeling en `charging_location_id` de bredere: een aanbod dat over
 * de hub zelf gaat en nog niet aan één vloot hangt.
 *
 * `quotes_traject_check` staat op `<= 1`: hoogstens één traject, en geen is ook
 * goed — een aanbod dat nog niet aan een traject hangt is normaal. Zie de
 * keuzeregel in docs/adr/0001.
 *
 * ── LET OP BIJ HET SCHRIJVEN ─────────────────────────────────────────────────
 *
 * Zet bij een wissel altijd de andere twee kolommen expliciet op `null`.
 * `trajectPatch()` hieronder doet dat; gebruik die in plaats van één kolom te
 * zetten, anders blijft een oude waarde staan en gaat de CHECK af bij het
 * opslaan.
 */

export type TrajectKind = 'project' | 'kavel' | 'opportunity' | 'geen';

export type TrajectRef = {
  kind: TrajectKind;
  id: string | null;
};

type TrajectColumns = {
  project_id?: string | null;
  charging_location_id?: string | null;
  charging_opportunity_id?: string | null;
};

/** De drie kolommen van de traject-as, als patch voor een update. */
export type TrajectPatch = {
  project_id: string | null;
  charging_location_id: string | null;
  charging_opportunity_id: string | null;
};

/** Aan welk traject hangt deze offerte, en met welke id? */
export function traject(quote: TrajectColumns | null | undefined): TrajectRef {
  if (quote?.project_id) return { kind: 'project', id: quote.project_id };
  if (quote?.charging_location_id) return { kind: 'kavel', id: quote.charging_location_id };
  if (quote?.charging_opportunity_id) return { kind: 'opportunity', id: quote.charging_opportunity_id };
  return { kind: 'geen', id: null };
}

/**
 * Het soort dat een keuzelijst toont. Zolang er nog geen entiteit is gekozen,
 * staan alle drie de kolommen leeg en zegt `traject()` dus "geen". Het soort dat
 * de gebruiker net koos moet dan blijven staan, anders verschijnt de tweede lijst
 * (welk project, welke kavel) nooit en lijkt de keuze niet te werken.
 */
export function zichtbaarSoort(huidig: TrajectRef, gekozenSoort: TrajectKind | null): TrajectKind {
  return huidig.kind !== 'geen' ? huidig.kind : gekozenSoort ?? 'geen';
}

const KIND_LABEL: Record<TrajectKind, string> = {
  project: 'Project',
  kavel: 'Kavel',
  opportunity: 'Opportunity',
  geen: 'Geen traject',
};

/** Korte aanduiding van het traject-soort, zonder de naam van de entiteit. */
export function trajectKindLabel(kind: TrajectKind): string {
  return KIND_LABEL[kind];
}

/**
 * Volledig label voor lijsten en koppen: "Project — JWM Metaalservice B.V.".
 * `naam` komt van de aanroeper, want die heeft de entiteit al opgezocht.
 */
export function trajectLabel(traject: TrajectRef, naam?: string | null): string {
  if (traject.kind === 'geen') return KIND_LABEL.geen;
  return naam ? `${KIND_LABEL[traject.kind]} — ${naam}` : KIND_LABEL[traject.kind];
}

/**
 * Zet één traject en leeg de andere twee. Altijd alle drie de kolommen, zodat er
 * nooit twee tegelijk gevuld raken en `quotes_traject_check` niet afgaat.
 */
export function trajectPatch(kind: TrajectKind, id: string | null): TrajectPatch {
  return {
    project_id: kind === 'project' ? id : null,
    charging_location_id: kind === 'kavel' ? id : null,
    charging_opportunity_id: kind === 'opportunity' ? id : null,
  };
}

/** De kolomnaam die bij een traject-soort hoort, voor een `.eq()` in een query. */
export function trajectColumn(
  kind: Exclude<TrajectKind, 'geen'>
): 'project_id' | 'charging_location_id' | 'charging_opportunity_id' {
  switch (kind) {
    case 'project':
      return 'project_id';
    case 'kavel':
      return 'charging_location_id';
    case 'opportunity':
      return 'charging_opportunity_id';
  }
}
